
import "../../css/styles.css";
import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { useContext } from "react";
import { UserContext } from "../reuseableComponents/auth/UserContext";
import { Accordion, Switch, Button } from "@utahdts/utah-design-system";
import { GET_ALL_SITE_VISIT_SECTIONS } from "../../queries/siteVisitQrys/siteVisitSectionsAndQuestionsQrys";
import SectionQuestions from "./SectionQuestions";
import QuestionModal from "./QuestionModal";
import SectionModal from "./SectionModal";
import SiteVisitTypeModal from "./SiteVisitTypeModal";

const ManageSectionsAndQuestions = () => {
  const { user } = useContext(UserContext);
  const canView = user?.permissions?.includes("view_survey_question");
  const canEdit = user?.permissions?.includes("edit_survey_question");

  const { data, loading, error } = useQuery(GET_ALL_SITE_VISIT_SECTIONS);
  const sections = data?.getAllSiteVisitSections ?? [];

  const [showInactiveSections, setShowInactiveSections] = useState(false);
  const [showInactiveQuestions, setShowInactiveQuestions] = useState(false);
  const [selectedQuestionSection, setSelectedQuestionSection] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [siteVisitTypeOpen, setSiteVisitTypeOpen] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const [allSectionsOpen, setAllSectionsOpenState] = useState(true);

  const isSectionOpen = (section) => openSections[section.sectionId] ?? true;

  const setAllSectionsOpen = (isOpen) => {
    setAllSectionsOpenState(isOpen);
    setOpenSections(sections.reduce((acc, section) => ({ ...acc, [section.sectionId]: isOpen }), {}));
  };

  const handleSectionToggle = (sectionId, previousIsOpen) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: !previousIsOpen }));
  };

  if (error || !canView) {
    return (
      <div className="page-container">
        <h1>Manage Sections and Questions</h1>
        <p>Could not load sections and questions. {!canView ? "You do not have permissions to view this page" : error?.message}
        </p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1>Manage Sections and Questions</h1>
      {loading && <p>Loading...</p>}
      {!loading && (
        <div className="utah-design-system">
          <div className="page-header">
            <div className="header-controls">
              <div className="header-buttons">
                {canEdit && (
                  <>
                    <Button appearance="solid"
                      color="secondary"
                      onClick={() => setSelectedSection({})}
                    >New Section</Button>
                    <Button appearance="outlined"
                      color="secondary"
                      onClick={() => setSiteVisitTypeOpen(true)}
                    >New Site Visit Type</Button>
                  </>
                )}

              </div>
            </div>
            <div className="header-toggles">
              <Switch
                id="toggleInactiveSection"
                label="Show Inactive Sections"
                onChange={(e) => setShowInactiveSections(e.target.checked)}
                value={showInactiveSections}
                width={30}
              />
              <Switch
                id="toggleInactiveQuestions"
                label="Show Inactive Questions"
                onChange={(e) => setShowInactiveQuestions(e.target.checked)}
                value={showInactiveQuestions}
                width={30}
              />
                <Button appearance="solid"
                  className="accordion-toggle"
                  color="none"
                  iconRight={<span
                    className={`utds-icon-before-double-arrow-${allSectionsOpen ? 'up' : 'down'}`}
                    aria-hidden="true"
                  />}
                  onClick={() => setAllSectionsOpen(!allSectionsOpen)}
                >{allSectionsOpen ? 'Collapse All' : 'Expand All'}</Button>
            </div>
          </div>

          {sections.filter(s => showInactiveSections || s.status !== 'inactive').map((section) => (
            <Accordion key={section.sectionId}
              className="section-and-questions"
              headerClassName="section-header"
              contentClassName="section-questions"
              headingLevel={4}
              headerContent={
                <div className="section-header-content">
                  <span className="section-orderNum">{section.orderNum}</span>
                  <span className="section-description">{section.description}</span>
                  <span className="section-status">{section.status}</span>
                {canEdit && (
                  <>
                  <a href={`#edit-section-${section.sectionId}`} className="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedSection(section); }}
                  >Edit Section</a>
                  <a href={`#add-questioin-${section.sectionId}`} className="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedQuestionSection(section); }}
                  >Add Question</a>
                  </>
                )}

                </div>
              }
              isOpen={isSectionOpen(section)}
              onToggle={(previousIsOpen) => handleSectionToggle(section.sectionId, previousIsOpen)}
            >
              <SectionQuestions
                sectionId={section.sectionId}
                questions={section.siteVisitQuestions}
                showInactive={showInactiveQuestions}
                onQuestionClick={(question) => {
                  setSelectedQuestionSection(section);
                  setSelectedQuestion(question);
                }}
              />
            </Accordion>
          ))}
        </div>
      )}
      {selectedSection && (
        <SectionModal
          section={selectedSection}
          canEdit={canEdit}
          onClose={() => setSelectedSection(null)}
          onSectionChange={(updatedSection) => setSelectedSection(updatedSection)}
        />
      )}
      {siteVisitTypeOpen && (
        <SiteVisitTypeModal
          isOpen={siteVisitTypeOpen}
          canEdit={canEdit}
          onClose={() => setSiteVisitTypeOpen(null)}
        />
      )}
      {(selectedQuestionSection || selectedQuestion) && (
        <QuestionModal
          section={selectedQuestionSection}
          questionSummary={selectedQuestion}
          canEdit={canEdit}
          onClose={() => { setSelectedQuestionSection(null); setSelectedQuestion(null); }}
        />
      )}
    </div>
  );
};

export default ManageSectionsAndQuestions;
